import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, Download, X, FileText, Share2, Printer } from 'lucide-react';
import { DanfeResponse } from '@/services/orderApiService';
import { printPdfFromBase64 } from '@/services/printService';
import { useToast } from '@/hooks/use-toast';

interface PDVDanfeViewerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  danfeData: DanfeResponse | null;
  isLoading: boolean;
  error: string | null;
  orderId: number | null;
}

const PDVDanfeViewer: React.FC<PDVDanfeViewerProps> = ({
  open,
  onOpenChange,
  danfeData,
  isLoading,
  error,
  orderId
}) => {
  const { toast } = useToast();

  const handleDownloadPdf = () => {
    if (!danfeData?.danfe) return;
    
    try {
      // Create a link element to download the PDF
      const link = document.createElement('a');
      link.href = `data:application/pdf;base64,${danfeData.danfe}`;
      link.download = `danfe-venda-${orderId || 'documento'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error downloading PDF:', err);
    }
  };

  /**
   * Função para imprimir o DANFE diretamente
   */
  const handlePrintPdf = async () => {
    if (!danfeData?.danfe) return;
    
    try {
      await printPdfFromBase64({
        pdfBase64: danfeData.danfe,
        filename: `danfe-venda-${orderId || 'documento'}.pdf`
      });
      
      toast({
        title: "DANFE enviado para impressão",
        description: "O documento foi enviado para a impressora configurada"
      });
    } catch (err) {
      console.error('Error printing PDF:', err);
      toast({
        title: "Erro ao imprimir",
        description: err instanceof Error ? err.message : "Não foi possível imprimir o documento",
        variant: "destructive"
      });
    }
  };

  const handleSharePdf = async () => {
    if (!danfeData?.danfe) return;
    
    try {
      if (navigator.share) {
        // Convert base64 to blob for sharing
        const base64Response = await fetch(`data:application/pdf;base64,${danfeData.danfe}`);
        const blob = await base64Response.blob();
        
        // Create file from blob
        const file = new File([blob], `danfe-venda-${orderId || 'documento'}.pdf`, { type: 'application/pdf' });
        
        await navigator.share({
          title: `DANFE da Venda #${orderId || ''}`,
          files: [file]
        });
      } else {
        // Fallback for browsers that don't support navigator.share
        alert('Compartilhamento não suportado neste navegador. Por favor, use o botão de download.');
      }
    } catch (err) {
      console.error('Error sharing PDF:', err);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <FileText className="h-5 w-5 mr-2 text-red-500" />
            DANFE da Venda {orderId && `#${orderId}`}
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-auto min-h-[400px] flex items-center justify-center border rounded-md bg-gray-50">
          {isLoading ? (
            <div className="text-center p-10">
              <Loader2 className="h-10 w-10 animate-spin mx-auto mb-4 text-indigo-600" />
              <p>Carregando DANFE...</p>
            </div>
          ) : error ? (
            <div className="text-center p-10 text-red-500">
              <p className="font-semibold mb-2">Erro ao carregar DANFE</p>
              <p className="text-sm">{error}</p>
              <p className="text-sm mt-2">Verifique a NF no retaguarda</p>
            </div>
          ) : danfeData && danfeData.success && danfeData.danfe ? (
            <iframe 
              src={`data:application/pdf;base64,${danfeData.danfe}`} 
              className="w-full h-full min-h-[400px]"
              title="DANFE PDF"
            />
          ) : (
            <div className="text-center p-10 text-gray-500">
              <FileText className="h-16 w-16 mx-auto mb-4 text-gray-400" />
              <p className="font-semibold mb-2">DANFE não disponível</p>
              <p className="text-sm">
                {danfeData?.mensagem || "Não foi possível obter o documento fiscal para esta venda."}
              </p>
              <p className="text-sm mt-2">Verifique a NF no retaguarda</p>
            </div>
          )}
        </div>
        
        <DialogFooter className="flex flex-wrap justify-between gap-2 mt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            <X className="h-4 w-4 mr-2" />
            Fechar
          </Button>
          
          <div className="flex flex-wrap gap-2">
            {danfeData?.danfe && (
              <>
                <Button 
                  variant="outline" 
                  onClick={handlePrintPdf}
                  className="bg-blue-50 hover:bg-blue-100 border-blue-200"
                >
                  <Printer className="h-4 w-4 mr-2 text-blue-600" />
                  Imprimir
                </Button>
                
                <Button 
                  variant="outline" 
                  onClick={handleSharePdf}
                  className="bg-purple-50 hover:bg-purple-100 border-purple-200"
                >
                  <Share2 className="h-4 w-4 mr-2 text-purple-600" />
                  Compartilhar
                </Button>
                
                <Button onClick={handleDownloadPdf} className="bg-green-600 hover:bg-green-700">
                  <Download className="h-4 w-4 mr-2" />
                  Baixar PDF
                </Button>
              </>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PDVDanfeViewer;
